import React from 'react';
import styled from 'styled-components';
import { 
  FaCheck, 
  FaExclamationTriangle, 
  FaInfoCircle, 
  FaTimes, 
  FaPlus, 
  FaMinus 
} from 'react-icons/fa';
import { useToast } from '../context/ToastContext';

// Toast notification system
const ToastContainerWrapper = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Toast = styled.div`
  background: ${props => {
    switch (props.type) {
      case 'success': return '#d4edda';
      case 'error': return '#f8d7da';
      case 'info': return '#d1ecf1';
      case 'warning': return '#fff3cd';
      default: return '#d1ecf1';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'success': return '#155724';
      case 'error': return '#721c24';
      case 'info': return '#0c5460';
      case 'warning': return '#856404';
      default: return '#0c5460';
    }
  }};
  border: 1px solid ${props => {
    switch (props.type) {
      case 'success': return '#c3e6cb';
      case 'error': return '#f5c6cb';
      case 'warning': return '#ffeaa7';
      default: return '#bee5eb';
    }
  }};
  border-radius: 6px;
  padding: ${props => props.collapsed ? '0.5rem' : '1rem'};
  min-width: ${props => props.collapsed ? '60px' : '300px'};
  max-width: ${props => props.collapsed ? '60px' : '400px'};
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: slideIn 0.3s ease-out;
  transition: all 0.3s ease;
  overflow: hidden;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const ToastIcon = styled.div`
  font-size: ${props => props.collapsed ? '1rem' : '1.2rem'};
  flex-shrink: 0;
`;

const ToastContent = styled.div`
  flex: 1;
  display: ${props => props.collapsed ? 'none' : 'block'};
`;

const ToastTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const ToastMessage = styled.div`
  font-size: 0.9rem;
  opacity: 0.9;
  margin-bottom: ${props => props.hasProgress ? '0.5rem' : '0'};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(0,0,0,0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 0.5rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${props => {
    switch (props.type) {
      case 'success': return '#28a745';
      case 'error': return '#dc3545';
      case 'info': return '#17a2b8';
      case 'warning': return '#ffc107';
      default: return '#17a2b8';
    }
  }};
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  font-size: 0.8rem;
  color: inherit;
  opacity: 0.8;
  margin-top: 0.25rem;
`;

const ToastActions = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: center;
  flex-shrink: 0;
`;

const ToastButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font-size: ${props => props.collapsed ? '0.8rem' : '1rem'};
  padding: 0.25rem;
  opacity: 0.7;
  border-radius: 3px;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 1;
    background: rgba(255,255,255,0.2);
  }
`;

const CollapsedToast = styled.div`
  display: ${props => props.collapsed ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 0.8rem;
  font-weight: 600;
`;

const GlobalToastContainer = () => {
  const { toasts, removeToast, toggleToastCollapse } = useToast();

  return (
    <ToastContainerWrapper>
      {toasts.map(toast => (
        <Toast key={toast.id} type={toast.type} collapsed={toast.collapsed}>
          <ToastIcon collapsed={toast.collapsed}>
            {toast.type === 'success' && <FaCheck />}
            {toast.type === 'error' && <FaExclamationTriangle />}
            {toast.type === 'info' && <FaInfoCircle />}
            {toast.type === 'warning' && <FaExclamationTriangle />}
          </ToastIcon>

          <ToastContent collapsed={toast.collapsed}>
            <ToastTitle>{toast.title}</ToastTitle>
            <ToastMessage hasProgress={toast.progress !== null && toast.progress !== undefined}>
              {toast.message}
            </ToastMessage>
            {toast.progress !== null && toast.progress !== undefined && (
              <ProgressBar>
                <ProgressFill type={toast.type} progress={toast.progress} />
              </ProgressBar>
            )}
            {toast.progress !== null && toast.progress !== undefined && (
              <ProgressText>{Math.round(toast.progress)}%</ProgressText>
            )}
          </ToastContent>

          <CollapsedToast collapsed={toast.collapsed}>
            {toast.type === 'success' && '✓'}
            {toast.type === 'error' && '✗'}
            {toast.type === 'info' && 'ℹ'}
            {toast.type === 'warning' && '⚠'}
          </CollapsedToast>

          <ToastActions>
            <ToastButton
              collapsed={toast.collapsed}
              onClick={() => toggleToastCollapse(toast.id)}
              title={toast.collapsed ? 'Expand' : 'Collapse'}
            >
              {toast.collapsed ? <FaPlus /> : <FaMinus />}
            </ToastButton>
            <ToastButton
              collapsed={toast.collapsed}
              onClick={() => removeToast(toast.id)}
              title="Close"
            >
              <FaTimes />
            </ToastButton>
          </ToastActions>
        </Toast>
      ))}
    </ToastContainerWrapper>
  );
};

export default GlobalToastContainer; 